import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { selectRequestsAreLoading } from 'state'
import { TagCategoryEnum, TagCategoryTargetEnum, selectTagById } from 'state/constants'
import {
    PartiposterTag,
    PartiposterTagType,
    ProfileToTagGroupedById,
    TagGroup,
    clearPartiposterTagList,
    closeTagHistoryModal,
    getPartiposterTagListRequest,
    openDeleteTagModal,
    openTagModal,
    selectPartiposterTagsById,
    selectPartiposterTagsUuid,
    selectTagHistoryModal,
    updatePartiposterTagRequest,
} from 'state/partiposter'
import { STATE_KEYS } from 'state/requests'
import _ from 'lodash'
import Modal from 'particles/Modal'
import styled from 'particles/themes/styled'
import Text from 'particles/Text'
import RichInfoTag, { getSeverityState } from 'particles/Tag'
import { TAG_LABELS } from '.'
import RectangleButton from 'particles/Button/RectangleButton'
import AddComment from 'particles/Comment/AddComment'
import MenuItem from 'particles/MenuItem'
import { formatBackendDate } from 'services/dates'
import { selectSelectedCampaignSummary } from 'state/review'
import { selectCMUserUuid, selectIsInternalUser } from 'state/profile'
import Icon from 'components/Icon/Icon'
import Menu from 'particles/Menu'
import Loading from 'components/Loading'
import UserProfile from './UserProfile'
import { getProfileTagIconType } from './TagModals'
import { Tag } from 'services/api/constants'
import { useWindowSize } from 'hooks'
import { usePermission } from 'hooks/permissions'
import { CampaignSubmission } from 'components/Shared/Permission/enums'

const TagAndCommentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[100]};
    .flex-column {
        display: flex;
        flex-direction: column;
    }
    .header {
        display: flex;
        justify-content: space-between;
    }
    .comments {
        gap: ${({ theme }) => theme.spacing[400]};
        .comment {
            gap: ${({ theme }) => theme.spacing[100]};
        }
    }
`

interface PartiposterTagComponentProps {
    tagGroup: TagGroup
    partiposterTag: PartiposterTag
}

const PartiposterTagComponent: React.FC<PartiposterTagComponentProps> = ({ tagGroup, partiposterTag }) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
    const [commentBlockWidth, setCommentBlockWidth] = useState(0)
    const tagModalHistory = useSelector(selectTagHistoryModal)
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)
    const cmUserUuid = useSelector(selectCMUserUuid)
    const handleOnEditClickRef = useRef<() => void>()
    const windowSize = useWindowSize()
    const containerRef = useRef<HTMLDivElement>(null)
    const canManageTag = usePermission(CampaignSubmission.ManageTag)

    const tag = tagGroup.tag

    const handleSaveComment = (comment?: string) => {
        dispatch(
            updatePartiposterTagRequest({
                tag_uuid: tag.uuid,
                text: comment,
                uuid: partiposterTag.uuid,
                submissionUuid: tagModalHistory.submissionUuid,
                profileUuid: tagModalHistory.selectedProfile.uuid,
            }),
        )
    }
    const isDeletable =
        _.isEmpty(partiposterTag.submission_uuid) ||
        canManageTag ||
        cmUserUuid === partiposterTag.creator_user_info.uuid

    const handleOnDeleteClick = () => {
        dispatch(
            openDeleteTagModal({
                ...tagModalHistory,
                isOpen: true,
                selectedTag: tag,
                uuid: partiposterTag.uuid,
                nextPartiposterTag: tagGroup.partiposterTags.length > 1 ? tagGroup.partiposterTags[1] : undefined,
            }),
        )
        setMenuAnchorEl(null)
    }

    const handleMoreAction = (e: React.MouseEvent<HTMLElement>, onHideButtonsCallback: () => void) => {
        e.stopPropagation()
        handleOnEditClickRef.current = () => {
            setMenuAnchorEl(null)
            onHideButtonsCallback()
        }
        setMenuAnchorEl(e.currentTarget)
    }

    const handleOnCloseMenu = () => setMenuAnchorEl(null)

    useEffect(() => {
        if (containerRef.current) {
            const width = containerRef.current.clientWidth
            if (width !== commentBlockWidth) {
                setCommentBlockWidth(width)
            }
        }
    }, [windowSize])

    return (
        <div className="comment flex-column" ref={containerRef}>
            {partiposterTag.brand_name && (
                <Text scale="caption700" color="secondary">
                    {`${partiposterTag.brand_name} - ${partiposterTag.campaign_title}`}
                </Text>
            )}
            <AddComment
                width={commentBlockWidth}
                profile={partiposterTag.last_updater_user_info}
                value={partiposterTag.text}
                placeholder={t('submission.tagHistory.noComments')}
                timeSlots={
                    selectedCampaignSummary
                        ? formatBackendDate(partiposterTag.updated_at, selectedCampaignSummary.country)
                        : undefined
                }
                primaryButtonLabel={t('submission.table.savePartiposterLabel')}
                secondaryButtonLabel={t('helper.uploadProgress.inProgress.button.cta1')}
                onPrimaryButtonClick={handleSaveComment}
                hidableButtons
                isCommentItem
                moreActionCallback={handleMoreAction}
                hideReadOnlyText={!partiposterTag.text}
            />
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleOnCloseMenu}>
                <MenuItem.Text text={t('button.edit')} onClick={handleOnEditClickRef.current} />
                {isDeletable && <MenuItem.Text text={t('button.remove')} onClick={handleOnDeleteClick} />}
            </Menu>
        </div>
    )
}

interface TagAndCommentProps {
    tag: Tag
    tagGroup: TagGroup
    latestPartiposterTag: PartiposterTag
}

const TagAndComment: React.FC<TagAndCommentProps> = ({ tag, tagGroup, latestPartiposterTag }) => {
    const [t] = useTranslation()
    const [isShowAll, setIsShowAll] = useState(false)
    const isProfileTag = _.isEmpty(latestPartiposterTag.submission_uuid)
    const cmUserUuid = useSelector(selectCMUserUuid)
    const isInternalUser = useSelector(selectIsInternalUser)

    const canViewTagDetails =
        tagGroup.partiposterTags.length &&
        (isInternalUser || cmUserUuid === latestPartiposterTag.creator_user_info.uuid)

    const handleShowAll = () => {
        setIsShowAll(!isShowAll)
    }

    return (
        <>
            <TagAndCommentContainer>
                <div className="header">
                    <RichInfoTag
                        state="hover"
                        icon={getProfileTagIconType(tag.name as TagCategoryEnum)}
                        contentType={getSeverityState(tag.severity)}
                        number={isProfileTag ? undefined : tagGroup.partiposterTags.length}
                        label={
                            tag.category.target === TagCategoryTargetEnum.PROFILE_TYPE || tag.is_custom
                                ? tag.name
                                : TAG_LABELS[tag.name]
                        }
                    />
                    {tagGroup.partiposterTags.length > 1 && canViewTagDetails ? (
                        <RectangleButton
                            size="s"
                            color="neutral"
                            form="text"
                            icon={isShowAll ? 'caretUp' : 'caretDown'}
                            label={isShowAll ? t('button.showLess') : t('button.showAll')}
                            onClick={handleShowAll}
                        />
                    ) : null}
                </div>
                {canViewTagDetails && (
                    <div className="comments flex-column">
                        <PartiposterTagComponent partiposterTag={latestPartiposterTag} tagGroup={tagGroup} />
                        {isShowAll &&
                            _.without(tagGroup.partiposterTags, latestPartiposterTag).map((partiposterTag, index) => (
                                <PartiposterTagComponent
                                    key={`partiposter-tag-${index}`}
                                    tagGroup={tagGroup}
                                    partiposterTag={partiposterTag}
                                />
                            ))}
                    </div>
                )}
            </TagAndCommentContainer>
            <div className="line" />
        </>
    )
}

const EmptyContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    .text-container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[100]};
        align-items: center;
    }
`

const Empty: React.FC = () => {
    const [t] = useTranslation()
    return (
        <EmptyContainer>
            <Icon.NotFoundIcon />
            <div className="text-container">
                <Text scale="H4">{t('modalWindow.noTagsHeader')}</Text>
                <Text scale="caption400">{t('modalWindow.noTagsSubText')}</Text>
            </div>
        </EmptyContainer>
    )
}

interface HistoryProps {
    className?: string
}

const HistoryModal: React.FC<HistoryProps> = ({ className }) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const tagModalHistory = useSelector(selectTagHistoryModal)
    const { isOpen, selectedProfile, campaignUuid, submissionUuid } = tagModalHistory
    const tagById = useSelector(selectTagById)
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.GetPartiposterTagHistoryList]))
    const partiposterTagsUuid = useSelector(selectPartiposterTagsUuid(PartiposterTagType.HISTORY))
    const partiposterTagsById = useSelector(selectPartiposterTagsById(PartiposterTagType.HISTORY))
    const onClose = () => dispatch(closeTagHistoryModal())
    const onOpenAddTag = () =>
        dispatch(
            openTagModal({
                ...tagModalHistory,
                isOpen: true,
            }),
        )

    const [BadTagsComponent, allPartiposterTagsId, allPartiposterTags] = useMemo(() => {
        const BadTags: JSX.Element[] = []
        const badTags = new Set<string>()
        const allTagsId: string[] = []
        const allTags: ProfileToTagGroupedById = {}
        partiposterTagsUuid.forEach(tagUuid => {
            const tag = partiposterTagsById[tagUuid]
            if (tagById[tag.tag_uuid].severity === 'bad') {
                badTags.add(tag.tag_uuid)
            }
            if (allTags[tag.tag_uuid]) {
                allTags[tag.tag_uuid] = {
                    ...allTags[tag.tag_uuid],
                    partiposterTags: [...allTags[tag.tag_uuid].partiposterTags, tag],
                }
            } else {
                allTagsId.push(tag.tag_uuid)
                allTags[tag.tag_uuid] = {
                    tag: tagById[tag.tag_uuid],
                    partiposterTags: [tag],
                }
            }
        })

        badTags.forEach((tag_uuid, index) => {
            const tagGroup = allTags[tag_uuid]
            const tag = tagGroup.tag
            const badTagCount = tagGroup.partiposterTags.length
            const latestPartiposterTag = tagGroup.partiposterTags[0]
            const isProfileTag = _.isEmpty(latestPartiposterTag.submission_uuid)
            BadTags.push(
                <RichInfoTag
                    key={index}
                    state="hover"
                    icon={getProfileTagIconType(tag.name as TagCategoryEnum)}
                    contentType={getSeverityState(tag.severity)}
                    number={isProfileTag ? undefined : badTagCount}
                    label={
                        tag.category.target === TagCategoryTargetEnum.PROFILE_TYPE || tag.is_custom
                            ? tag.name
                            : TAG_LABELS[tag.name]
                    }
                />,
            )
        })

        return [BadTags, allTagsId, allTags]
    }, [partiposterTagsUuid])

    useEffect(() => {
        if (selectedProfile) {
            dispatch(
                getPartiposterTagListRequest({
                    profileUuid: selectedProfile.uuid,
                    type: PartiposterTagType.HISTORY,
                    campaignOrdering: campaignUuid,
                }),
            )
        }
        return () => {
            dispatch(clearPartiposterTagList({ type: PartiposterTagType.HISTORY }))
        }
    }, [selectedProfile])

    const isTagEmpty = allPartiposterTagsId && allPartiposterTagsId.length === 0

    return isOpen ? (
        <Modal
            className={className}
            hasIcon={false}
            title={t('modalWindow.tagHistoryTitle')}
            onClose={onClose}
            hasSecondaryButton={false}
            size="m"
            hasButtonSet={!!campaignUuid && !!submissionUuid}
            primaryButtonProps={{ label: t('button.addTags') }}
            onConfirm={onOpenAddTag}
        >
            {!isLoading && isTagEmpty ? (
                <Empty />
            ) : (
                <div className="allTags">
                    <UserProfile profileData={selectedProfile} />
                    {isLoading ? (
                        <Loading />
                    ) : (
                        <>
                            <Text>{t('modalWindow.submission.tagHistoryWarning')}</Text>
                            {BadTagsComponent.length > 0 && <div className="bad-tags">{BadTagsComponent}</div>}
                            <div className="line" />
                            {allPartiposterTagsId.map((tag_uuid, index) => {
                                const tagGroup = allPartiposterTags[tag_uuid]
                                const tag = tagGroup.tag
                                const latestPartiposterTag = tagGroup.partiposterTags[0]
                                return (
                                    <TagAndComment
                                        key={`tag-comment-container-${index}`}
                                        tag={tag}
                                        tagGroup={tagGroup}
                                        latestPartiposterTag={latestPartiposterTag}
                                    />
                                )
                            })}
                        </>
                    )}
                </div>
            )}
        </Modal>
    ) : null
}

export default styled(HistoryModal)`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[600]};
    .allTags {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[300]};
        .bad-tags {
            display: flex;
            flex-wrap: wrap;
            gap: ${({ theme }) => theme.spacing[200]};
        }
        .line {
            border-width: 0px;
            border-top-width: 1px;
            border-style: solid;
            border-color: ${({ theme }) => theme.colors.divider.primary};
        }
    }
`
